<template>
    <div>
        <a-form :form="form" @submit="handleSubmit">
            <a-card :bordered="false" class="header-solid h-full" :bodyStyle="{ paddingTop: 0, paddingBottom: '40px' }">
                <template #title>
                    <h5 class="mb-0">Load details</h5>
                </template>
                <a-row type="flex" :gutter="24">
                    <a-col :span="12" :lg="12" class="mb-12">
                        <a-row :gutter="[24]">
                            <a-col :span="12">
                                <a-form-item class="mb-10" label="Bid ID" :colon="false"
                                    :validate-status="errors.bid_id ? 'error' : ''" :help="errors.bid_id || ''">
                                    <a-input v-model="load.bid_id" placeholder="Bid ID" />
                                </a-form-item>
                            </a-col>
                            <a-col :span="12">
                                <a-form-item class="mb-10" label="External Load ID" :colon="false"
                                    :validate-status="errors.external_load_id ? 'error' : ''" :help="errors.external_load_id || ''">
                                    <a-input v-model="load.external_load_id" placeholder="External Load ID" />
                                </a-form-item>
                            </a-col>
                            <a-col :span="12">
                                <a-form-item class="mb-10" label="Scheduled Pickup Date Time" :colon="false"
                                    :validate-status="errors.scheduled_pickup_date_time ? 'error' : ''" :help="errors.scheduled_pickup_date_time || ''">
                                    <a-date-picker
                                        v-model="load.scheduled_pickup_date_time"
                                        show-time
                                        format="YYYY-MM-DD HH:mm:ss"
                                        placeholder="Select pickup date and time"
                                        style="width: 100%"
                                    />
                                </a-form-item>
                            </a-col>
                            <a-col :span="12">
                                <a-form-item class="mb-10" label="Bidding Round Ends (minutes)" :colon="false"
                                    :validate-status="errors.bidding_round_ends ? 'error' : ''" :help="errors.bidding_round_ends || ''">
                                    <a-input-number
                                        v-model="load.bidding_round_ends"
                                        placeholder="Enter minutes until bidding ends"
                                        :min="0"
                                        :max="10080"
                                        style="width: 100%"
                                        :precision="0"
                                    />
                                </a-form-item>
                            </a-col>
                            <a-col :span="12">
                                <a-form-item class="mb-10" label="Origin City" :colon="false"
                                    :validate-status="errors.origin_city_name ? 'error' : ''" :help="errors.origin_city_name || ''">
                                    <a-input v-model="load.origin_city_name" placeholder="Origin City Name" />
                                </a-form-item>
                            </a-col>
                            <a-col :span="12">
                                <a-form-item class="mb-10" label="Origin State" :colon="false"
                                    :validate-status="errors.origin_state_code ? 'error' : ''" :help="errors.origin_state_code || ''">
                                    <a-input v-model="load.origin_state_code" placeholder="State Code (e.g., CA)" maxlength="2" />
                                </a-form-item>
                            </a-col>
                            <a-col :span="12">
                                <a-form-item class="mb-10" label="Destination City" :colon="false"
                                    :validate-status="errors.destination_city_name ? 'error' : ''" :help="errors.destination_city_name || ''">
                                    <a-input v-model="load.destination_city_name" placeholder="Destination City Name" />
                                </a-form-item>
                            </a-col>
                            <a-col :span="12">
                                <a-form-item class="mb-10" label="Destination State" :colon="false"
                                    :validate-status="errors.destination_state_code ? 'error' : ''" :help="errors.destination_state_code || ''">
                                    <a-input v-model="load.destination_state_code" placeholder="State Code (e.g., NY)" maxlength="2" />
                                </a-form-item>
                            </a-col>
                            <a-col :span="12">
                                <a-form-item class="mb-10" label="Total Distance (miles)" :colon="false"
                                    :validate-status="errors.total_distance ? 'error' : ''" :help="errors.total_distance || ''">
                                    <a-input v-model="load.total_distance" placeholder="Total Distance" />
                                </a-form-item>
                            </a-col>
                            <a-col :span="12">
                                <a-form-item class="mb-10" label="Rate Adjustment Amount" :colon="false">
                                    <a-input v-model="load.rate_adjustment_amount" placeholder="Rate Adjustment Amount (optional)" />
                                </a-form-item>
                            </a-col>
                            <a-col :span="24">
                                <a-form-item class="mb-10" label="In Transit Stops" :colon="false">
                                    <a-input v-model="load.in_transit_stops" placeholder="In Transit Stops (optional)" />
                                </a-form-item>
                            </a-col>
                            <a-col :span="24">
                                <a-form-item class="mb-10" label="Origin Location Description" :colon="false">
                                    <a-textarea v-model="load.origin_location_description" placeholder="Origin Location Description (optional)" rows="3" />
                                </a-form-item>
                            </a-col>
                        </a-row>
                    </a-col>
                    <a-col :span="12" :lg="12" class="mb-12">
                        <a-row :gutter="[24]">
                            <a-col :span="24">
                                <a-card size="small" title="Load Summary" style="margin-bottom: 16px;">
                                    <p><strong>Bid ID:</strong> {{ load.bid_id || 'Not set' }}</p>
                                    <p><strong>External Load ID:</strong> {{ load.external_load_id || 'Not set' }}</p>
                                    <p><strong>Origin:</strong> {{ getLocationString(load.origin_city_name, load.origin_state_code) }}</p>
                                    <p><strong>Destination:</strong> {{ getLocationString(load.destination_city_name, load.destination_state_code) }}</p>
                                    <p><strong>Distance:</strong> {{ load.total_distance || 'Not set' }} miles</p>
                                    <p><strong>Pickup Date:</strong> {{ formatDateTime(load.scheduled_pickup_date_time) }}</p>
                                    <p><strong>Bidding Ends:</strong> {{ formatBiddingEnds(load.bidding_round_ends) }}</p>
                                    <p><strong>Rate Adjustment:</strong> {{ load.rate_adjustment_amount ? '$' + load.rate_adjustment_amount : 'Not set' }}</p>
                                    <p><strong>In Transit Stops:</strong> {{ load.in_transit_stops || 'None' }}</p>
                                </a-card>
                            </a-col>
                            <!-- <a-col :span="24" v-if="isEdit">
                                <a-card size="small" title="Bids Information">
                                    <p v-if="load.bids && load.bids.length > 0">
                                        <strong>Total Bids:</strong> {{ load.bids.length }}
                                    </p>
                                    <p v-else>No bids received yet</p>
                                    <p v-if="load.lowest_bid">
                                        <strong>Lowest Bid:</strong> ${{ load.lowest_bid.amount }}
                                    </p>
                                </a-card>
                            </a-col> -->
                        </a-row>
                    </a-col>
                </a-row>
            </a-card>
            <a-button type="primary" html-type="submit" style="margin-top: 20px; margin-right: 8px;"> Submit </a-button>
            <a-button @click="handleCancel" style="margin-top: 20px;"> Cancel </a-button>
        </a-form>
    </div>
</template>

<script>
import router from "../../router/index.js";
import loadAPI from "../../api/load.js";
import moment from "moment";

// Fields required by the form (frontend-only enforcement). The backend itself
// only requires external_load_id and bidding_round_ends; everything below is
// enforced here for data quality. Rate Adjustment, In Transit Stops and Origin
// Location Description are intentionally left optional.
const REQUIRED_FIELDS = [
    { key: "bid_id", message: "Please input the Bid ID!" },
    { key: "external_load_id", message: "Please input the External Load ID!" },
    { key: "scheduled_pickup_date_time", message: "Please select the pickup date and time!" },
    { key: "bidding_round_ends", message: "Please enter the bidding round duration!" },
    { key: "origin_city_name", message: "Please input the origin city!" },
    { key: "origin_state_code", message: "Please input the origin state!" },
    { key: "destination_city_name", message: "Please input the destination city!" },
    { key: "destination_state_code", message: "Please input the destination state!" },
    { key: "total_distance", message: "Please input the total distance!" },
];

export default {
    data() {
        return {
            load: {
                bid_id: "",
                external_load_id: "",
                bidding_round_ends: null,
                rate_adjustment_amount: "",
                origin_city_name: "",
                origin_state_code: "",
                in_transit_stops: "",
                destination_city_name: "",
                destination_state_code: "",
                total_distance: "",
                scheduled_pickup_date_time: null,
                origin_location_description: "",
            },
            errors: {},
            loadEditData: null,
            isEdit: false,
            form: this.$form.createForm(this, { name: "editLoad" }),
        };
    },
    watch: {
        // Clear a field's warning as soon as the user gives it a value,
        // mirroring the inline validation behaviour on the sign-in form.
        load: {
            deep: true,
            handler() {
                const keys = Object.keys(this.errors);
                if (!keys.length) return;
                keys.forEach((key) => {
                    if (!this.isEmpty(this.load[key])) {
                        this.$delete(this.errors, key);
                    }
                });
            },
        },
    },
    methods: {
        async getLoadById(id) {
            return loadAPI.getLoadById(id);
        },
        isEmpty(value) {
            if (value === null || value === undefined) return true;
            if (typeof value === "string") return value.trim() === "";
            return false; // numbers (including 0) and moment objects count as set
        },
        validateForm() {
            const errors = {};
            REQUIRED_FIELDS.forEach(({ key, message }) => {
                if (this.isEmpty(this.load[key])) {
                    errors[key] = message;
                }
            });
            this.errors = errors;
            return Object.keys(errors).length === 0;
        },
        extractError(error, fallback) {
            return (
                (error && error.response && error.response.data && error.response.data.error) ||
                fallback
            );
        },
        formatDateTime(dateTime) {
            if (!dateTime) return 'Not set';
            return moment(dateTime).format('YYYY-MM-DD HH:mm:ss');
        },
        formatBiddingEnds(minutes) {
            if (!minutes && minutes !== 0) return 'Not set';
            const mins = parseInt(minutes);
            if (isNaN(mins)) return 'Invalid';

            if (mins >= 60) {
                const hours = Math.floor(mins / 60);
                const remainingMinutes = mins % 60;
                return `${hours}h ${remainingMinutes}m`;
            } else {
                return `${mins} minutes`;
            }
        },
        getLocationString(city, state) {
            if (!city && !state) return 'Not set';
            if (!city) return state;
            if (!state) return city;
            return `${city}, ${state}`;
        },
        handleSubmit(e) {
            e.preventDefault();

            // Frontend-only required-field validation (see REQUIRED_FIELDS above).
            if (!this.validateForm()) {
                this.$message.error('Please fill in all required fields.');
                return;
            }

            // Prepare load data for submission
            const loadData = {
                ...this.load,
                scheduled_pickup_date_time: this.load.scheduled_pickup_date_time ? moment(this.load.scheduled_pickup_date_time).format() : null,
                bidding_round_ends: this.load.bidding_round_ends ? parseInt(this.load.bidding_round_ends) : null,
            };

            // Request to create or update load
            if (!this.isEdit) {
                loadAPI
                    .createLoad(loadData)
                    .then((responseData) => {
                        if (responseData.status == 201) {
                            this.$message.success('Load created successfully');
                            router.push("/loads/loads-list");
                        }
                    })
                    .catch((error) => {
                        console.error('Error creating load:', error);
                        this.$message.error(this.extractError(error, 'Failed to create load. Please try again.'));
                    });
            } else {
                loadAPI
                    .updateLoad(loadData, this.loadEditData.id)
                    .then((responseData) => {
                        if (responseData.status == 200) {
                            this.$message.success('Load updated successfully');
                            router.push("/loads/loads-list");
                        }
                    })
                    .catch((error) => {
                        console.error('Error updating load:', error);
                        this.$message.error(this.extractError(error, 'Failed to update load. Please try again.'));
                    });
            }
        },

        handleCancel() {
            router.push("/loads/loads-list");
        },
    },
    async mounted() {
        if (this.$route.params.id !== undefined) {
            this.loadEditData = await this.getLoadById(this.$route.params.id); // get load data according to ID
            this.isEdit = true;
            this.load.bid_id = this.loadEditData.bid_id || '';
            this.load.external_load_id = this.loadEditData.external_load_id || '';
            this.load.bidding_round_ends = this.loadEditData.bidding_round_ends ? parseInt(this.loadEditData.bidding_round_ends) : null;
            this.load.rate_adjustment_amount = this.loadEditData.rate_adjustment_amount || '';
            this.load.origin_city_name = this.loadEditData.origin_city_name || '';
            this.load.origin_state_code = this.loadEditData.origin_state_code || '';
            this.load.in_transit_stops = this.loadEditData.in_transit_stops || '';
            this.load.destination_city_name = this.loadEditData.destination_city_name || '';
            this.load.destination_state_code = this.loadEditData.destination_state_code || '';
            this.load.total_distance = this.loadEditData.total_distance || '';
            this.load.scheduled_pickup_date_time = this.loadEditData.scheduled_pickup_date_time ? moment(this.loadEditData.scheduled_pickup_date_time) : null;
            this.load.origin_location_description = this.loadEditData.origin_location_description || '';
        }
    },
};
</script>

<style lang="scss" scoped>
// Load form specific styles
.ant-card {
    .ant-card-body {
        p {
            margin-bottom: 8px;

            strong {
                color: #1890ff;
            }
        }
    }
}
</style>
